import { Loader2, Inbox, ShieldCheck, AlertTriangle, Clock, CircleDashed, XCircle, RefreshCw, FileSearch } from "lucide-react";
import { Link } from "react-router-dom";
import { VERIF_LABEL } from "@/lib/fmt";

export function Logo({ light = false, compact = false }) {
  const c = light ? "#F7F3EB" : "#29483D";
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo" aria-label="VyapaarSetu by UrbanNexis home">
      <svg width="34" height="34" viewBox="0 0 40 40" aria-hidden="true">
        <rect width="40" height="40" rx="10" fill={light ? "rgba(247,243,235,.12)" : "#29483D"} />
        <path d="M7 26 C12 13, 28 13, 33 26" stroke="#F7F3EB" strokeWidth="2.4" fill="none" strokeLinecap="round" />
        <line x1="6" y1="26" x2="34" y2="26" stroke="#B59762" strokeWidth="2.4" strokeLinecap="round" />
        <line x1="14" y1="19.5" x2="14" y2="26" stroke="#F7F3EB" strokeWidth="1.6" />
        <line x1="20" y1="17.2" x2="20" y2="26" stroke="#F7F3EB" strokeWidth="1.6" />
        <line x1="26" y1="19.5" x2="26" y2="26" stroke="#F7F3EB" strokeWidth="1.6" />
      </svg>
      <span className="leading-none">
        <span className="font-display text-xl font-semibold block" style={{ color: c }}>VyapaarSetu</span>
        {!compact && <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: light ? "#E9DDCB" : "#B86545" }}>by UrbanNexis</span>}
      </span>
    </Link>
  );
}

export const Loading = ({ text = "Loading…" }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-stone-500" data-testid="loading-state" role="status">
    <Loader2 className="animate-spin" size={18} /> {text}
  </div>
);

export const Empty = ({ title, body, action, testid = "empty-state" }) => (
  <div className="card p-8 text-center" data-testid={testid}>
    <Inbox className="mx-auto mb-3 text-[#B59762]" />
    <p className="font-display text-lg text-[#29483D]">{title}</p>
    {body && <p className="text-sm text-stone-600 mt-1 max-w-md mx-auto">{body}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorBox = ({ msg }) => msg ? (
  <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" role="alert" data-testid="error-message">{msg}</div>
) : null;

const VSTYLE = {
  seller_provided: ["#F1ECE3", "#6B5B4E", CircleDashed], submitted: ["#EFE7D6", "#7A6331", Clock], under_review: ["#E7EEF0", "#2F5560", FileSearch],
  info_required: ["#FBEBD9", "#9A5A1E", AlertTriangle], reviewed_against_documents: ["#E4EEE7", "#29483D", ShieldCheck],
  independently_verified: ["#29483D", "#F7F3EB", ShieldCheck], rejected: ["#F7E1DA", "#9B3B22", XCircle], needs_revalidation: ["#F5E7C8", "#7A5A12", RefreshCw],
};
export function VerifPill({ status, testid }) {
  const [bg, fg, Icon] = VSTYLE[status] || VSTYLE.seller_provided;
  return <span className="chip" style={{ background: bg, color: fg }} data-testid={testid}><Icon size={12} />{VERIF_LABEL[status] || status}</span>;
}

const SSTYLE = { draft: "#EDE6DA", submitted: "#EFE3C5", changes_requested: "#FBE3CF", published: "#DCEBE2", rejected: "#F5DAD2", suspended: "#E8D5D5",
  withdrawn: "#E5E2DC", available: "#DCEBE2", under_offer: "#F3E2C4", sold: "#E1DDF0", open: "#E7EEF0", closed: "#DCEBE2", paid: "#DCEBE2",
  due: "#EFE3C5", invoiced: "#E7EEF0", overdue: "#F5DAD2", cancelled: "#E5E2DC", requested: "#EFE3C5", granted: "#DCEBE2", revoked: "#F5DAD2",
  denied: "#F5DAD2", pending: "#EFE3C5", approved: "#DCEBE2", accepted: "#DCEBE2", countered: "#F3E2C4" };
export const StatusPill = ({ s, testid }) => (
  <span className="chip text-[#292A25]" style={{ background: SSTYLE[s] || "#EDE6DA" }} data-testid={testid}>{String(s || "—").replace(/_/g, " ")}</span>
);

export const DemoBanner = ({ small }) => (
  <div className={`rounded-xl bg-[#49392F] text-[#F7F3EB] ${small ? "px-2 py-1 text-[10px]" : "px-3 py-2 text-xs"} font-semibold tracking-wide`} data-testid="demo-label">
    Demo listing — not available for purchase
  </div>
);

export const Section = ({ title, children, right, testid }) => (
  <section className="card p-5 sm:p-6" data-testid={testid}>
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="font-display text-lg md:text-xl text-[#29483D]">{title}</h2>{right}
    </div>
    {children}
  </section>
);

export const Stat = ({ label, value, hint, testid }) => (
  <div className="card p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-[#49392F]">{label}</p>
    <p className="font-display text-2xl text-[#29483D] mt-1">{value}</p>
    {hint && <p className="text-[11px] text-stone-500 mt-1">{hint}</p>}
  </div>
);

export const Field = ({ label, error, children, hint, id }) => (
  <div>
    {label && <label className="lbl" htmlFor={id}>{label}</label>}
    {children}
    {hint && !error && <p className="text-[11px] text-stone-500 mt-1">{hint}</p>}
    {error && <p className="text-xs text-[#9B3B22] mt-1" data-testid={`field-error-${id}`}>{error}</p>}
  </div>
);

export const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[#EFE6D8] text-sm last:border-0">
    <span className="text-stone-600 shrink-0 max-w-[50%]">{k}</span><span className="text-right font-medium min-w-0 break-words" data-testid={testid}>{v ?? "Not provided"}</span>
  </div>
);
